import * as React from "react";
import { useState, useEffect } from "react";
import { useUser } from "../state";
import { RouteComponentProps } from "react-router";
import { api } from "../api";
import { RLink } from "../DesignSystem/RLink";
import { GoldCoin } from "../Toolbar/GoldCoin";
import { SilverCoin } from "../Toolbar/SilverCoin";

const TabSelector = ({
  tabs,
  activeTab,
  onSelect,
}: {
  tabs: { label: string }[];
  activeTab: number;
  onSelect: (index: number) => void;
}) => {
  return (
    <div className="mb-6 flex gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
      {tabs.map((tab, i) => (
        <button
          key={i}
          onClick={() => onSelect(i)}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition ${
            i === activeTab
              ? "bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white"
              : "text-slate-600 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white"
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

export function EditProfile(_props: RouteComponentProps<{}>): JSX.Element {
  const userState = useUser();
  const user = userState.type === "logged-in" ? userState.user : undefined;

  const [activeTab, setActiveTab] = useState(0);
  const [sendEmail, setSendEmail] = useState<boolean | undefined>();
  const [sendNotificationEmails, setSendNotificationEmails] = useState<
    boolean | undefined
  >();
  const [playVisibility, setPlayVisibility] = useState<
    "all" | "over5" | "none" | undefined
  >();
  const [profile, setProfile] = useState<string | undefined>();
  const [files, setFiles] = useState<FileList | undefined>();
  const [newSaying, setNewSaying] = useState("");
  const [addedSaying, setAddedSaying] = useState(false);
  const [updateState, setUpdateState] = useState<"never" | "updating" | "done">(
    "never"
  );

  useEffect(() => {
    if (user) {
      setSendEmail(user.send_email);
      setSendNotificationEmails(user.send_notification_emails);
      setPlayVisibility(user.play_visibility);
      setProfile(user.profile);
    }
  }, [user]);

  if (userState.type === "loading") {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-white/10 dark:bg-slate-900">
          <i
            className="fa fa-spinner fa-pulse fa-2x text-slate-400"
            aria-hidden="true"
          ></i>
          <p className="mt-4 text-slate-500 dark:text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-white/10 dark:bg-slate-900">
          <p className="text-lg text-red-600 dark:text-red-400">
            Please log in to edit your profile.
          </p>
        </div>
      </div>
    );
  }

  const handleUpdateProfile = async () => {
    setUpdateState("updating");
    await api.postFile("/api/user/profile", {
      send_email: sendEmail ? "on" : "off",
      send_notification_emails: sendNotificationEmails ? "on" : "off",
      play_visibility: playVisibility || "none",
      profile: profile || "",
      file: (files && files[0]) || undefined,
    });
    setUpdateState("done");
  };

  const handleAddSaying = async () => {
    await api.post<"/api/saying">(`/api/saying`, {
      msg: newSaying,
    });

    setNewSaying("");
    setAddedSaying(true);
  };

  const tabs = [
    { label: "Edit profile" },
    { label: "Settings" },
    { label: "Gold benefits" },
  ];

  return (
    <div className="mx-auto max-w-4xl px-4 py-16">
      <div className="mb-8">
        <h1 className="text-4xl font-semibold text-slate-900 dark:text-white">
          Profile
        </h1>
        <p className="mt-2 text-slate-500 dark:text-slate-400">
          {user.username} · Level {user.level}
        </p>
      </div>

      <TabSelector tabs={tabs} activeTab={activeTab} onSelect={setActiveTab} />

      {activeTab === 0 && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900">
            <h3 className="mb-6 text-xl font-semibold text-slate-900 dark:text-white">
              Profile Information
            </h3>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <img
                  src={user.avatar_url}
                  className="h-16 w-16 rounded-lg object-cover"
                  alt="Profile avatar"
                />
                <div>
                  <div className="text-lg font-medium text-slate-900 dark:text-white">
                    LV {user.level} {user.username}
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    {user.experience} XP · {user.expToNextLevel} to next level{" "}
                    <RLink
                      className="text-chips-gold hover:underline"
                      to="/explain-xp"
                    >
                      (What is XP?)
                    </RLink>
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  About you
                </label>
                <textarea
                  rows={5}
                  value={profile || ""}
                  onChange={(e) => setProfile(e.currentTarget.value)}
                  placeholder="Write some stuff here about yourself!"
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-chips-gold focus:outline-none focus:ring-2 focus:ring-chips-gold/20 dark:border-white/10 dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Profile picture (50x50 recommended)
                </label>
                <input
                  type="file"
                  onChange={(e) => {
                    setFiles(e.target.files || undefined);
                  }}
                  className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 file:mr-4 file:rounded-md file:border-0 file:bg-chips-gold file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-amber-600 focus:border-chips-gold focus:outline-none focus:ring-2 focus:ring-chips-gold/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900">
            <button
              onClick={handleUpdateProfile}
              className="inline-flex items-center gap-2 rounded-lg bg-chips-gold px-6 py-3 font-semibold text-white transition hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-chips-gold/20"
            >
              {updateState === "updating" && (
                <i className="fa fa-spinner fa-pulse" aria-hidden="true"></i>
              )}
              Save
            </button>

            {updateState === "done" && (
              <span className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <i className="fa fa-check" aria-hidden="true"></i>
                <span className="font-medium">Saved!</span>
              </span>
            )}

            <RLink
              to={`/user/${user.username}`}
              className="text-sm font-medium text-slate-600 hover:text-chips-gold dark:text-slate-400 dark:hover:text-chips-gold"
            >
              ← Return to profile
            </RLink>
          </div>
        </div>
      )}

      {activeTab === 1 && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900">
            <h3 className="mb-6 text-xl font-semibold text-slate-900 dark:text-white">
              Notification Settings
            </h3>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="send_email"
                  onChange={(e) => setSendEmail(e.currentTarget.checked)}
                  checked={sendEmail}
                  className="h-4 w-4 rounded border-slate-300 text-chips-gold focus:ring-2 focus:ring-chips-gold/20 dark:border-white/10"
                />
                <label
                  htmlFor="send_email"
                  className="text-sm text-slate-700 dark:text-slate-300"
                >
                  Email me about new compos.
                </label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="send_notification_emails"
                  onChange={(e) =>
                    setSendNotificationEmails(e.currentTarget.checked)
                  }
                  checked={sendNotificationEmails}
                  className="h-4 w-4 rounded border-slate-300 text-chips-gold focus:ring-2 focus:ring-chips-gold/20 dark:border-white/10"
                />
                <label
                  htmlFor="send_notification_emails"
                  className="text-sm text-slate-700 dark:text-slate-300"
                >
                  Email me about activity on Chips related to me (comments on my
                  song, or someone mentioning me).
                </label>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900">
            <h3 className="mb-6 text-xl font-semibold text-slate-900 dark:text-white">
              Privacy Settings
            </h3>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Publicize my play history
              </label>
              <select
                onChange={(e) =>
                  setPlayVisibility(
                    e.currentTarget.value as "all" | "over5" | "none"
                  )
                }
                value={playVisibility}
                className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2 text-slate-900 focus:border-chips-gold focus:outline-none focus:ring-2 focus:ring-chips-gold/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
              >
                <option value="all">Let people see everything</option>
                <option value="over5">
                  Only songs I've played more than 5 times
                </option>
                <option value="none">Never</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900">
            <button
              onClick={handleUpdateProfile}
              className="inline-flex items-center gap-2 rounded-lg bg-chips-gold px-6 py-3 font-semibold text-white transition hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-chips-gold/20"
            >
              {updateState === "updating" && (
                <i className="fa fa-spinner fa-pulse" aria-hidden="true"></i>
              )}
              Save Settings
            </button>

            {updateState === "done" && (
              <span className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <i className="fa fa-check" aria-hidden="true"></i>
                <span className="font-medium">Saved!</span>
              </span>
            )}
          </div>
        </div>
      )}

      {activeTab === 2 && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-xl font-semibold text-slate-900 dark:text-white">
                Your Balance
              </h3>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-4 py-2 dark:bg-amber-900/20">
                  <span className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                    {user.gold}
                  </span>
                  <GoldCoin />
                </div>
                <div className="flex items-center gap-2 rounded-lg bg-slate-100 px-4 py-2 dark:bg-slate-700">
                  <span className="text-2xl font-bold text-slate-500 dark:text-slate-300">
                    {user.silver}
                  </span>
                  <SilverCoin />
                </div>
              </div>
            </div>

            <p className="mb-4 text-slate-600 dark:text-slate-400">
              Gold is awarded for participating in the Chips community. Leave
              helpful comments and reviews, vote, and submit entries to earn
              more!
            </p>

            <div className="rounded-lg bg-slate-50 p-4 dark:bg-slate-800/50">
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Ways to earn gold{" "}
                <span className="font-normal normal-case">
                  (1 <GoldCoin /> = 20 <SilverCoin />)
                </span>
              </h4>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
                <li className="flex items-center gap-2">
                  <span className="font-semibold text-amber-600 dark:text-amber-400">
                    +2 <SilverCoin />
                  </span>
                  Vote on an entry
                </li>
                <li className="flex items-center gap-2">
                  <span className="font-semibold text-amber-600 dark:text-amber-400">
                    +10+ <SilverCoin />
                  </span>
                  Leave a review on an entry
                </li>
                <li className="flex items-center gap-2">
                  <span className="font-semibold text-amber-600 dark:text-amber-400">
                    +5 <GoldCoin />
                  </span>
                  Vote on every track in a compo
                </li>
                <li className="flex items-center gap-2">
                  <span className="font-semibold text-amber-600 dark:text-amber-400">
                    +10 <GoldCoin />
                  </span>
                  Get a track past judgement (based on score)
                </li>
              </ul>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900">
            <h3
              className={`mb-4 flex items-center gap-2 text-xl font-semibold ${
                user.gold >= 30
                  ? "text-slate-900 dark:text-white"
                  : "text-slate-400 dark:text-slate-600"
              }`}
            >
              Add secret message{" "}
              <span className="text-base font-normal">
                (30 <GoldCoin />)
              </span>
            </h3>

            <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
              Add a message to the footer taglines that appears randomly for all
              users!
            </p>

            {addedSaying && (
              <div className="mb-4 flex items-center gap-2 rounded-lg bg-green-50 px-4 py-2 text-green-700 dark:bg-green-900/20 dark:text-green-400">
                <i className="fa fa-check" aria-hidden="true"></i>
                Added!
              </div>
            )}

            <div className="space-y-4">
              <input
                type="text"
                value={newSaying}
                onChange={(e) => setNewSaying(e.currentTarget.value)}
                placeholder="Your secret message..."
                disabled={user.gold < 30}
                className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2 text-slate-900 placeholder-slate-400 focus:border-chips-gold focus:outline-none focus:ring-2 focus:ring-chips-gold/20 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
              />

              {user.gold >= 30 ? (
                <button
                  onClick={handleAddSaying}
                  className="inline-flex items-center gap-2 rounded-lg bg-chips-gold px-6 py-3 font-semibold text-white transition hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-chips-gold/20"
                >
                  Add it
                </button>
              ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  You need {30 - user.gold} more gold to unlock this feature.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
